import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fetchParticipation } from '@/components/stats/PlayedToggle';
import { sortByJersey } from './gameUtils';
import { statsGameApi } from './statsGameApi';
import { EditRosterTeam } from './EditRosterTeam';

const seedDrafts = (game) => {
  const seed = { a: {}, b: {} };
  for (const [k, tk] of [['a', 'team_a'], ['b', 'team_b']]) {
    (game?.[tk]?.players || []).forEach(p => {
      seed[k][p.id] = { name: p.name || '', jersey_number: p.jersey_number || '' };
    });
  }
  return seed;
};

const sortedTeam = (src, fallbackName) => {
  const team = src || { name: fallbackName, players: [] };
  return { ...team, players: [...(team.players || [])].sort(sortByJersey) };
};

// Mid-game fixes for names, jersey numbers and Played / DNP. Player IDs stay stable so stats stay linked.
export function EditRosterModal({ game, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [playedOrig, setPlayedOrig] = useState({});
  const [playedDraft, setPlayedDraft] = useState({});
  const [drafts, setDrafts] = useState(() => seedDrafts(game));

  useEffect(() => {
    fetchParticipation(game.id).then(parts => {
      const seed = Object.fromEntries(Object.entries(parts).map(([pid, v]) => [pid, v.played]));
      setPlayedOrig(seed);
      setPlayedDraft(seed);
    });
  }, [game.id]);

  const teamA = useMemo(() => sortedTeam(game?.team_a, 'Team A'), [game?.team_a]);
  const teamB = useMemo(() => sortedTeam(game?.team_b, 'Team B'), [game?.team_b]);

  const updateField = (teamKey, playerId, field, value) => {
    setDrafts(prev => ({
      ...prev,
      [teamKey]: { ...prev[teamKey], [playerId]: { ...prev[teamKey][playerId], [field]: value } },
    }));
  };
  const togglePlayed = (pid) => setPlayedDraft(prev => ({ ...prev, [pid]: !prev[pid] }));

  const savePlayer = async (teamKey, player) => {
    const draft = drafts[teamKey][player.id] || {};
    const originalName = (player.name || '').trim();
    const originalJersey = (player.jersey_number || '').trim();
    const nextName = (draft.name || '').trim();
    const nextJersey = (draft.jersey_number || '').trim();
    const playedChanged = player.id in playedDraft && playedDraft[player.id] !== playedOrig[player.id];
    if (nextName === originalName && nextJersey === originalJersey && !playedChanged) return;
    if (!nextName) {
      toast.error(`${player.name || 'Player'} needs a name.`);
      return;
    }
    const body = {};
    if (nextName !== originalName) body.name = nextName;
    if (nextJersey !== originalJersey) body.jersey_number = nextJersey;
    if (playedChanged) body.played = playedDraft[player.id];
    const res = await statsGameApi.patchPlayer(game.id, player.id, body);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Failed to update ${player.name || 'player'}`);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      // Sequential so an error stops at the player that failed.
      for (const p of teamA.players || []) await savePlayer('a', p);
      for (const p of teamB.players || []) await savePlayer('b', p);
      toast.success('Roster updated. Existing stats stay linked to each player.');
      if (typeof onSaved === 'function') await onSaved();
      onClose();
    } catch (e) {
      toast.error(e.message || 'Failed to save roster changes');
    } finally {
      setSaving(false);
    }
  };

  const teamProps = { onField: updateField, playedDraft, onTogglePlayed: togglePlayed };

  return (
    <div className="fixed inset-0 z-[115] bg-[#0A0F2C]/95 backdrop-blur-sm flex flex-col" data-testid="edit-roster-modal">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
        <button onClick={onClose}
          className="text-white/70 hover:text-white text-sm font-medium flex items-center gap-1"
          data-testid="edit-roster-cancel">
          <ArrowLeft className="w-4 h-4" /> Cancel
        </button>
        <div className="flex-1 text-white font-bold text-base">Edit Players</div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <p className="text-xs text-white/60 leading-relaxed">
          Fix a player's jersey number or spelling mid-game. Previously
          recorded stats stay attached to each player automatically.
        </p>
        <p className="text-xs text-white/60 leading-relaxed mt-2" data-testid="edit-roster-dnp-help">
          Players who never check in or record a stat are marked <span className="font-bold text-white/80">DNP</span> and
          won't count toward their games played. Tap Played / DNP to override.
        </p>
        <EditRosterTeam teamKey="a" team={teamA} drafts={drafts.a} {...teamProps} />
        <EditRosterTeam teamKey="b" team={teamB} drafts={drafts.b} {...teamProps} />
      </div>
      <div className="p-4 border-t border-white/10 bg-[#001A5C]/70">
        <Button onClick={handleSaveAll} disabled={saving}
          className="w-full bg-amber-400 hover:bg-amber-300 text-black font-bold"
          data-testid="edit-roster-save">
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}
